import { motion } from "framer-motion";
import { ShieldCheck, Droplets, Zap, Wind } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SectionHeading } from "./SectionHeading";

const FAQS = [
    {
        q: "Is delivery and setup really free?",
        a: "Yes — within Genesee County, delivery, professional setup, and takedown are included in every rental. We arrive 1–2 hours before your party starts and handle everything.",
    },
    {
        q: "What happens if it rains?",
        a: "Michigan weather is wild — we get it. If the forecast looks rough, you can reschedule to any available date within 90 days at no charge. Just call us by 8pm the night before.",
    },
    {
        q: "Do I need a deposit?",
        a: "We'll take a small deposit to lock your date, applied to your total. The rest is due on delivery day — cash, card, or Venmo.",
    },
    {
        q: "What about power?",
        a: "Each inflatable needs one standard 110V outlet within 75 feet. No outlet? No problem — we rent quiet generators too.",
    },
    {
        q: "Can inflatables go on concrete or pavement?",
        a: "Grass is best, but we can set up on pavement, driveways, or gym floors using sandbag anchors instead of stakes. Just let us know your surface when booking.",
    },
    {
        q: "How clean are the units?",
        a: "Every inflatable is vacuumed, washed, and sanitized with kid-safe cleaner after every single rental. We inspect seams, blowers, and anchors before each setup.",
    },
];

const SAFETY = [
    { icon: ShieldCheck, text: "Fully insured & every unit safety-inspected before setup" },
    { icon: Wind, text: "We cancel or anchor down for winds over 15 mph — no exceptions" },
    { icon: Droplets, text: "Sanitized with kid-safe cleaner after every rental" },
    { icon: Zap, text: "GFCI-protected blowers and secured cords on every job" },
];

export const Faq = () => (
    <section id="faq" data-testid="faq-section" className="border-y-2 border-monster-navy bg-monster-navy py-24 sm:py-32">
        <div className="mx-auto grid max-w-6xl gap-14 px-5 lg:grid-cols-[1fr_0.8fr]">
            <div>
                <SectionHeading number="02" title="Rules of the" highlight="bounce." sub="Everything parents ask us before the party." light />
                <Accordion type="single" collapsible className="space-y-4">
                    {FAQS.map((f, i) => (
                        <AccordionItem
                            key={i}
                            value={`faq-${i}`}
                            data-testid={`faq-item-${i}`}
                            className="overflow-hidden rounded-3xl border-2 border-monster-navy bg-white px-6 shadow-sticker-sm"
                        >
                            <AccordionTrigger data-testid={`faq-trigger-${i}`} className="py-5 text-left font-display text-base font-semibold text-monster-navy hover:text-monster-blue hover:no-underline">
                                {f.q}
                            </AccordionTrigger>
                            <AccordionContent className="pb-5 text-sm leading-relaxed text-monster-navy/70">{f.a}</AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>
            <motion.aside
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ type: "spring", stiffness: 80, damping: 15 }}
                className="h-fit rounded-[32px] border-2 border-monster-navy bg-monster-yellow p-8 shadow-sticker lg:sticky lg:top-32"
            >
                <h3 className="font-display text-2xl font-semibold text-monster-navy">Safety first, fun always</h3>
                <p className="mt-2 text-sm font-semibold text-monster-navy/60">The boring stuff we take seriously so you don't have to.</p>
                <ul className="mt-7 space-y-5">
                    {SAFETY.map((s) => (
                        <li key={s.text} className="flex items-start gap-4">
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border-2 border-monster-navy bg-white shadow-sticker-sm">
                                <s.icon className="h-5 w-5 text-monster-blue" />
                            </span>
                            <span className="pt-1.5 text-sm font-bold leading-snug text-monster-navy/85">{s.text}</span>
                        </li>
                    ))}
                </ul>
            </motion.aside>
        </div>
    </section>
);
